import { i18n_locale as i18nLocale, LONG_UNIT_DISPLAY_ONLY_LOCALES, i18n_t as $t } from '@/utils/i18n';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';

export function pluralize (str, count) {
    if (!str.trim()) {
        return '';
    }
    if (!str.endsWith('s') && count !== 1) {
        return `${str}s`;
    }
    if (str.endsWith('s') && count === 1) {
        return str.slice(0, -1);
    }

    return str;
}

export function pluralizeSI (str, count) {
    if (!isFinite(count)) {
        return 'N/A';
    }

    return `${formatNumberWithSuffix(count)} ${pluralize(str, count)}`;
}

export function formatNumberWithSuffix (
    number,
    { max = 10000, precision = 3, maxFractionDigits = 2, style = 'decimal', percentFormat = 'short' } = {},
) {
    if (isNaN(number)) {
        return number;
    }

    if (style === 'percent') {
        if (number < 0.001) {
            return Intl.NumberFormat(i18nLocale, {
                maximumSignificantDigits: 1,
                style,
            }).format(number);
        }
        if (number < 0.01) {
            return Intl.NumberFormat(i18nLocale, {
                maximumSignificantDigits: 2,
                style,
            }).format(number);
        }
        if (number < 0.1) {
            return Intl.NumberFormat(i18nLocale, {
                maximumSignificantDigits: percentFormat === 'short' ? 2 : 3,
                style,
            }).format(number);
        }
        if (number < 1) {
            return Intl.NumberFormat(i18nLocale, {
                maximumSignificantDigits: percentFormat === 'short' ? 2 : 4,
                style,
            }).format(number);
        }

        return Intl.NumberFormat(i18nLocale, { maximumFractionDigits: 0, maximumSignificantDigits: 3, style }).format(
            number,
        );
    }

    if (number < max) {
        return Intl.NumberFormat(i18nLocale, { maximumFractionDigits: maxFractionDigits, style }).format(number);
    }

    const options = {
        notation: 'compact',
        maximumSignificantDigits: precision,
        maximumFractionDigits: maxFractionDigits,
        style,
    };

    return Intl.NumberFormat(i18nLocale, options).format(number);
}

export function formatRevenue (number) {
    const options = {
        style: 'currency',
        currency: 'USD',
    };
    if (number > 10000) {
        options.notation = 'compact';
        options.maximumSignificantDigits = 3;
    }

    return Intl.NumberFormat(undefined, options).format(number);
}

export const SECOND = 1000;

export const MINUTE = 60 * SECOND;

export const HOUR = MINUTE * 60;

export const DAY = HOUR * 24;

export function prettyTime (input, type = 'short', limitUnits = 2, useI18n = false) {
    if (isNumber(input)) {
        let timeStamp = input;
        const prettyStrComponents = {};

        if (timeStamp >= DAY) {
            const day = Math.floor(timeStamp / DAY);
            timeStamp -= day * DAY;
            prettyStrComponents.day = day;
        }

        if (timeStamp >= HOUR) {
            const hour = Math.floor(timeStamp / HOUR);
            timeStamp -= hour * HOUR;
            if (hour >= 24) {
                prettyStrComponents.day = (prettyStrComponents.day || 0) + Math.floor(hour / 24);
                prettyStrComponents.hour = hour % 24;
            } else {
                prettyStrComponents.hour = hour;
            }
        }

        if (timeStamp >= MINUTE) {
            const minute = Math.floor(timeStamp / MINUTE);
            timeStamp -= minute * MINUTE;

            prettyStrComponents.minutes = minute % 60;

            if (minute >= 60) {
                prettyStrComponents.hour = (prettyStrComponents.hour || 0) + Math.floor(minute / 60);
            }
        }

        if (timeStamp >= SECOND) {
            const sec = type === 'long' ? Math.floor(timeStamp / SECOND) : Math.round(timeStamp / SECOND);
            timeStamp -= sec * SECOND;

            prettyStrComponents.sec = sec % 60;

            if (sec >= 60) {
                const extraMinutes = Math.floor(sec / 60);
                prettyStrComponents.minutes = (prettyStrComponents.minutes || 0) + extraMinutes;
            }
        }

        if (timeStamp > 0) {
            const ms = Math.floor(timeStamp);
            if (ms > 0 && (Object.keys(prettyStrComponents).length === 0 || type === 'long')) {
                if (ms >= 1000) {
                    prettyStrComponents.sec = (prettyStrComponents.sec || 0) + Math.floor(ms / 1000);
                    prettyStrComponents.ms = ms % 1000;
                } else {
                    prettyStrComponents.ms = ms;
                }
            }
        }

        const unitDisplay =
            i18nLocale?.value && LONG_UNIT_DISPLAY_ONLY_LOCALES.includes(i18nLocale.value) ? 'long' : 'narrow';

        if (input === 0) {
            return useI18n
                ? new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'second', unitDisplay }).format(0)
                : '0s';
        }

        if (useI18n) {
            const formatters = {
                day: new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'day', unitDisplay }),
                hour: new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'hour', unitDisplay }),
                minutes: new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'minute', unitDisplay }),
                sec: new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'second', unitDisplay }),
                ms: new Intl.NumberFormat(i18nLocale?.value, { style: 'unit', unit: 'millisecond', unitDisplay }),
            };

            return Object.keys(prettyStrComponents)
                .reduce((acc, key, idx) => {
                    if (prettyStrComponents[key] > 0 && (idx < limitUnits || type === 'long')) {
                        return `${acc + formatters[key].format(prettyStrComponents[key])} `;
                    }

                    return acc;
                }, '')
                .trim();
        }

        return Object.keys(prettyStrComponents)
            .reduce(
                (acc, key, idx) =>
                    acc +
                    (prettyStrComponents[key] > 0 && (idx < limitUnits || type === 'long')
                        ? `${prettyStrComponents[key]}${key !== 'ms' ? key[0] : key} `
                        : ''),
                '',
            )
            .trim();
    }

    return '--';
}

export function simplePadZero (num) {
    return num < 10 ? `0${num}` : `${num}`;
}

export function prettyTimestamp (ms) {
    if (!ms || ms <= 0) {
        return '00:00:00';
    }

    const hour = Math.floor(ms / HOUR);
    ms %= HOUR;

    const minute = Math.floor(ms / MINUTE);
    ms %= MINUTE;

    const second = Math.floor(ms / SECOND);

    return `${simplePadZero(hour)}:${simplePadZero(minute)}:${simplePadZero(second)}`;
}

export function trimmedPrettyTimestamp (time) {
    time = prettyTimestamp(time);

    // Remove the leading 00 from hh:mm:ss time
    if (time.length === 8 && time.startsWith('00')) {
        time = time.slice(3);
    }

    return time;
}

/** Wall-clock time for replay event / dev log rows (agg `browserTime`, ms epoch; subscription TZ). */
export function prettyReplayUserTimeEventLog (browserTimeMs) {
    return moment(browserTimeMs).format(DATE_FORMAT.replayUserTimeEventLog);
}

/** Wall-clock for replay player clock / hints (absolute ms epoch; subscription TZ). */
export function prettyReplayUserTimeFull (wallClockMs) {
    if (wallClockMs == null || !Number.isFinite(wallClockMs)) {
        return '';
    }

    return moment(wallClockMs).format(DATE_FORMAT.replayUserTimeFull);
}

export function formatValueForSchema (value, schema, dateFormat = 'full') {
    if (!schema) {
        return value;
    }

    switch (schema) {
        case 'responseCount':
            if (!isNumber(value)) {
                return value || '';
            }

            return `${value} responses`;
        case 'nps':
            if (!isNumber(value)) {
                return value || '';
            }

            return Math.round(value * 100);
        case 'time':
        case 'date':
            if (!isNumber(value)) {
                return value || '';
            }

            if (value === 0) {
                return '';
            }

            if ([undefined, null, NaN, Infinity, -Infinity].includes(value)) {
                return '--';
            }

            return moment(value).format(DATE_FORMAT[dateFormat]);
        case 'list':
            if (value && value.join) {
                return value.join(', ');
            }

            return value;
        case 'prettyTime':
            if (isNumber(value) && value === 0) {
                return '--';
            }

            return prettyTime(value, 'short', 2, true);
        case 'guideStatus':
            // Remove the "guide" prefix
            return (value || '').replace(/^guide/, '');
        case 'boolean':
            // ! pendo-table's default sorting method will not sort the return value from the method properly!
            // ! use sortBy prop in the column settings to define sorting actions.
            if (value === true || (typeof value === 'string' && value.toLowerCase().trim() === 'true')) {
                return 'True';
            }
            if (value === false || (typeof value === 'string' && value.toLowerCase().trim() === 'false')) {
                return 'False';
            }

            return value;
        case 'yesNo': {
            if (value === true || (typeof value === 'string' && value.toLowerCase().trim() === 'true')) {
                return 'Yes';
            }
            if (value === false || (typeof value === 'string' && value.toLowerCase().trim() === 'false')) {
                return 'No';
            }

            return value;
        }
        case 'number':
            return value || 0;
        case 'percentage':
            if (!isNumber(value)) {
                return 'N/A';
            }

            return `${value}%`;
        case 'duration':
            if (value >= DAY || value >= HOUR) {
                return prettyTime(value, 'short', 2, true);
            }
            if (value >= MINUTE) {
                return prettyTime((value / MINUTE) * MINUTE, 'short', 2, true);
            }
            if (value < MINUTE && value >= SECOND) {
                return `${prettyTime(Math.floor(value / SECOND) * SECOND, 'short', 2, true)}`;
            }
            if (value < SECOND) {
                return `${prettyTime(SECOND, 'short', 2, true)}`;
            }

            return '--';
        case 'guideCount':
        case 'pageCount':
        case 'featureCount':
            return uniq(value).length;
        case 'revenue':
            value = Number(value);
            if (isNaN(value) || !isNumber(value)) return 'N/A';

            return `${formatRevenue(value)}`;
        default:
            if (value === null) {
                return '';
            }

            return value;
    }
}

export function addCommasToNumber (num) {
    const decimalPlaces = String(num).split('.')[1]?.length || 0;

    return new Intl.NumberFormat(i18nLocale?.value, {
        useGrouping: true,
        maximumFractionDigits: decimalPlaces,
    }).format(num);
}

export function largestRemainderRound (numbers, total = 100, decimals = 0) {
    // hacked together implementation of https://stackoverflow.com/a/13483710
    const multiplier = 10 ** decimals;
    total *= multiplier;
    const result = numbers
        .map((number, index) => {
            number *= multiplier;

            return {
                floor: Math.floor(number),
                remainder: getRemainder(number, decimals + 4),
                index,
            };
        })
        .sort((a, b) => b.remainder - a.remainder);
    const lowerSum = result.reduce((sum, current) => {
        return sum + current.floor;
    }, 0);
    const delta = total - lowerSum;
    if (delta <= result.length) {
        for (let i = 0; i < delta; i++) {
            result[i].floor++;
        }
    }

    return result.sort((a, b) => a.index - b.index).map((result) => (result.floor * 1.0) / multiplier);
}

function getRemainder (number, decimals = 4) {
    const remainder = number - Math.floor(number);

    return remainder.toFixed(decimals);
}

export function formatNumberAsText (value, decimalCount) {
    const num = parseFloat(value).toFixed(decimalCount);
    if (isNaN(num)) {
        return value;
    }

    return num;
}

export function roundPercentage (value) {
    if (value > 0 && value < 1) {
        return Number(value.toFixed(2));
    }

    return Math.round(value);
}

export function formatPercentage (number, maxDecimalDigits = 2) {
    return Intl.NumberFormat(i18nLocale?.value, {
        maximumFractionDigits: maxDecimalDigits,
        style: 'percent',
    }).format(number);
}

export function formatRegion (value) {
    // For now this is only used for region groupings in reporting, the value is coming back with the country code first then the region code (ex: US-nc).
    // Eventually we will want to add better region formatting across the app.
    if (value === '(none)' || !value) {
        return '(none)';
    }

    return value.toUpperCase();
}

export function formatCountry (value) {
    if (!value) {
        return value;
    }

    const countryNamesInEnglish = new Intl.DisplayNames(i18nLocale, { type: 'region' });

    return `${value} (${countryNamesInEnglish.of(value)})`;
}

const urlValidationRegex = new RegExp('^(http|https|chrome-extension)://');

export const urlValidationRule = () => [
    {
        required: true,
        message: $t(
            'formatters.Start the URL with http://, https://, or chrome-extension://.',
        ),
        pattern: urlValidationRegex,
        trigger: ['blur', 'change'],
    },
];

export function prettifyPartialJson (input) {
    if (!input.trim().match(/^[{[]/) && !input.includes('":')) {
        return input;
    }

    const IND = '    ';
    let result = '';
    let level = 0;
    let inString = false;
    let escaped = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inString) {
            result += char;
            if (escaped) {
                escaped = false;
            } else if (char === '\\') {
                escaped = true;
            } else if (char === '"') {
                inString = false;
            }
        } else {
            switch (char) {
                case '"':
                    inString = true;
                    result += '"';
                    break;

                case '{':
                case '[': {
                    // Check if next non-whitespace char is the closing brace
                    const nextChar = input[i + 1];
                    const closingChar = char === '{' ? '}' : ']';

                    if (nextChar === closingChar) {
                        // Empty object/array - keep on same line
                        result += char + closingChar;
                        i++; // Skip the closing brace
                    } else {
                        // Non-empty - add newline and indent
                        result += `${char}\n${IND.repeat(++level)}`;
                    }
                    break;
                }

                case '}':
                case ']':
                    result += `\n${IND.repeat(Math.max(--level, 0))}${char}`;
                    break;

                case ',':
                    result += `,\n${IND.repeat(level)}`;
                    break;

                case ':':
                    result += ': ';
                    break;

                case '\n':
                case '\r':
                    result += `\n${IND.repeat(level)}`;
                    break;

                case ' ':
                    if (inString) {
                        result += char;
                    }
                    break;

                default:
                    result += char;
            }
        }
    }

    return result;
}
